import * as Sentry from "@sentry/react";
import { TypeWireContainer } from "@typewirets/core";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
// start of style imports
import { currentEmbedHostRole } from "@/embed/embed-hosts";
import { apiRequestHost } from "@/lib/api";
import {
  registerChunkLoadErrorHandlers,
  reloadIfImportStalls,
  showStallNotice,
} from "./chunk-load-error-handler.ts";
import "./index.css";
import { sentryInitOptions } from "./sentry-options.ts";

// end of style imports

registerChunkLoadErrorHandlers();

async function main() {
  // Host to attach trace headers to. Derived from API_BASE rather than
  // VITE_API_URL: once an environment is flipped same-origin, API calls go to
  // the page's own host and VITE_API_URL no longer describes where the browser
  // talks (see apiRequestHost).
  const apiHost = apiRequestHost(window.location.host);

  const environment = import.meta.env.VITE_ENV || "dev";

  // One build serves the dashboard and its Salesforce embed address; the
  // host decides how much Session Replay may record (see sentry-options.ts).
  const hostRole = currentEmbedHostRole();

  if (environment !== "dev") {
    Sentry.init(
      sentryInitOptions({ environment, apiHost, hostKind: hostRole.kind }),
    );
  }

  const handleRenderError = (
    error: unknown,
    errorInfo: { componentStack?: string },
  ) => {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  };
  const rootOptions = {
    onCaughtError: handleRenderError,
    onUncaughtError: handleRenderError,
  };
  const rootElement = document.getElementById("root")!;

  // The dev server transforms modules on demand, so a cold first load has no
  // bound; the stall watchdog only makes sense for built bundles.
  const guardEntryImport = <T,>(pending: Promise<T>, stallText: string) =>
    import.meta.env.DEV
      ? pending
      : reloadIfImportStalls(pending, {
          onStall: () => {
            Sentry.captureMessage(
              "Entry chunk import stalled after reload",
              "error",
            );
            showStallNotice(rootElement, stallText);
          },
        });

  // Both apps are imported dynamically so the embed page never evaluates the
  // dashboard's modules (see `embed/embed-app.tsx`).
  if (hostRole.kind === "embed") {
    const { renderEmbedApp } = await guardEntryImport(
      import("./embed/embed-app.tsx"),
      "Upside is taking longer than usual to load.",
    );
    await renderEmbedApp(rootElement, hostRole.pair, rootOptions);
    return;
  }

  const [{ default: App }, { MainWires }] = await guardEntryImport(
    Promise.all([import("./App.tsx"), import("./main.wires.ts")]),
    "The dashboard is taking longer than usual to load.",
  );
  const container = new TypeWireContainer();
  await MainWires.apply(container);

  createRoot(rootElement, rootOptions).render(
    <StrictMode>
      <App container={container} />
    </StrictMode>,
  );
}

main();
